/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// GradientLineChart configurations
import configs from "examples/Charts/LineCharts/GradientLineChart/configs";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

const compactHeadingSx = {
  fontSize: "12px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const compactDataSx = {
  fontSize: "10px !important",
  fontWeight: "400 !important",
  lineHeight: "1.15 !important",
};

const compactCardSx = {
  width: "100%",
  height: "100%",
};

const compactHeaderSx = {
  minHeight: "28px",
  display: "flex",
  alignItems: "center",
};

function GradientLineChart({
  title = "",
  description = "",
  height = "19.125rem",
  chart,
  compact = false,
}) {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;

  useEffect(() => {
    const chartDatasets = chart.datasets
      ? chart.datasets.map((dataset) => ({
        ...dataset,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 3,
        borderColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        fill: true,
        maxBarThickness: 6,
        backgroundColor: gradientChartLine(
          chartRef.current.children[0],
          colors[dataset.color] ? colors[dataset.color || "dark"].main : colors.dark.main
        ),
      }))
      : [];

    setChartData(configs(chart.labels || [], chartDatasets, compact));
  }, [chart, compact]);

  const renderChart = (
    <SoftBox
      p={compact ? 1 : 2}
      sx={compact ? { height: "100%", display: "flex", flexDirection: "column" } : undefined}
    >
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={compact ? 1.5 : 1} sx={compact ? compactHeaderSx : undefined}>
              <SoftTypography
                variant="h6"
                fontWeight={compact ? "regular" : false}
                sx={compact ? compactHeadingSx : undefined}
              >
                {title}
              </SoftTypography>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography
              component="div"
              variant="button"
              fontWeight="regular"
              color="text"
              sx={compact ? compactDataSx : undefined}
            >
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox ref={chartRef} sx={{ height, minHeight: height }}>
            <Line data={data} options={options} />
          </SoftBox>
        ),
        [chartData, height]
      )}
    </SoftBox>
  );

  return title || description ? (
    <Card sx={compact ? compactCardSx : undefined}>{renderChart}</Card>
  ) : renderChart;
}


// Typechecking props for the GradientLineChart
GradientLineChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  compact: PropTypes.bool,
};

export default GradientLineChart;
